import React, { useState } from 'react';
import { CalendarHeart, MapPin, Clock, Check, X, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Renders a date-invite DM bubble with Accept/Decline (if the viewer is the recipient
 * and the invite is still pending), or the resolved status.
 *
 * Props:
 *   msg     : the DM message ({ id, sender_id, dateInvite: { kind, emoji, when, place, note, status } })
 *   mine    : true if the viewer sent the invite
 *   onUpdate: called with the refreshed message after accept/decline
 */
export default function DateInviteCard({ msg, mine, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const inv = msg.dateInvite || {};
  const status = inv.status || 'pending';
  const palette = mine
    ? { ring: 'rgba(244,114,182,0.55)', bg: 'linear-gradient(135deg, rgba(244,114,182,0.22), rgba(190,24,93,0.18))' }
    : { ring: 'rgba(251,113,133,0.55)', bg: 'linear-gradient(135deg, rgba(251,113,133,0.16), rgba(244,114,182,0.10))' };

  const respond = async (action) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dms/messages/${msg.id}/respond`, { action });
      onUpdate?.(data);
      toast.success(action === 'accept' ? 'Invite accepted 💕' : 'Invite declined');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not respond');
    } finally {
      setBusy(false);
    }
  };

  const whenText = inv.when
    ? new Date(inv.when).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div
      className="max-w-[78%] px-4 py-3 rounded-2xl text-sm w-[280px]"
      style={{ background: palette.bg, border: `1px solid ${palette.ring}` }}
      data-testid="date-invite-card"
    >
      <div className="flex items-center gap-2">
        <div className="text-3xl">{inv.emoji || '💌'}</div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-white flex items-center gap-1">
            <CalendarHeart size={13} className="text-rose-300" /> {inv.kind} date
          </div>
          <div className="text-[10px] text-white/60">{mine ? 'You sent' : 'You received'} an invite</div>
        </div>
      </div>

      {(whenText || inv.place) && (
        <div className="mt-2 space-y-1">
          {whenText && (
            <div className="text-[12px] text-white/85 flex items-center gap-1.5">
              <Clock size={11} className="text-rose-300" />{whenText}
            </div>
          )}
          {inv.place && (
            <div className="text-[12px] text-white/85 flex items-center gap-1.5">
              <MapPin size={11} className="text-rose-300" />{inv.place}
            </div>
          )}
        </div>
      )}

      {inv.note && (
        <div className="mt-2 text-[12px] text-white/75 italic">&ldquo;{inv.note}&rdquo;</div>
      )}

      {/* Status / actions */}
      {status === 'pending' && !mine && (
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => respond('accept')}
            disabled={busy}
            data-testid="date-invite-accept"
            className="flex-1 rounded-full py-1.5 text-xs font-semibold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white disabled:opacity-50 flex items-center justify-center gap-1"
          >
            <Check size={12} /> Accept
          </button>
          <button
            onClick={() => respond('decline')}
            disabled={busy}
            data-testid="date-invite-decline"
            className="flex-1 rounded-full py-1.5 text-xs font-semibold bg-white/10 hover:bg-white/15 text-white border border-white/15 flex items-center justify-center gap-1"
          >
            <X size={12} /> Decline
          </button>
        </div>
      )}

      {status === 'pending' && mine && (
        <div className="mt-3 text-[11px] text-white/50 flex items-center gap-1">
          <Sparkles size={10} className="text-rose-300" /> Awaiting their response…
        </div>
      )}

      {status === 'accepted' && (
        <div className="mt-3 text-[11px] font-semibold text-emerald-300 flex items-center gap-1" data-testid="date-invite-status-accepted">
          <Check size={12} /> Accepted 💕
        </div>
      )}

      {status === 'declined' && (
        <div className="mt-3 text-[11px] font-semibold text-rose-300 flex items-center gap-1" data-testid="date-invite-status-declined">
          <X size={12} /> Declined
        </div>
      )}
    </div>
  );
}
